import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text, Icon } from '@reservamos/elements';
import PropTypes from 'prop-types';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';

/**
 * BillingSuccess component.
 *
 * This component displays the success state after billing operations.
 *
 * @param {object} props - successData, onClose
 * @param {object} props.successData - Success data object with response details
 * @param {Function} props.onClose - Function to close the modal
 *
 * @returns {JSX.Element} The rendered billing success component.
 */
const BillingSuccess = ({ successData, onClose }) => {
  const { t } = useTranslation('exchange');
  const env = useWhitelabelEnvs();
  const { exchangeContactData } = env;

  /**
   * handlePhoneClick Function
   *
   * Handles phone number click to open phone dialer.
   *
   * @returns {void}
   */
  const handlePhoneClick = () => {
    if (exchangeContactData?.phone) {
      const phoneNumber = exchangeContactData.extension
        ? `${exchangeContactData.phone}${exchangeContactData.extension}`
        : exchangeContactData.phone;
      window.open(`tel:${phoneNumber}`, '_blank');
    }
  };

  /**
   * handleWhatsAppClick Function
   *
   * Handles WhatsApp click to open WhatsApp chat.
   *
   * @returns {void}
   */
  const handleWhatsAppClick = () => {
    if (exchangeContactData?.whatsapp) {
      const message =
        exchangeContactData.whatsappMessage || t('modal_confirm_billing.whatsapp_message');
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/${exchangeContactData.whatsapp}?text=${encodedMessage}`, '_blank');
    }
  };

  /**
   * handleEmailClick Function
   *
   * Handles email click to open email client.
   *
   * @returns {void}
   */
  const handleEmailClick = () => {
    if (exchangeContactData?.email) {
      window.open(`mailto:${exchangeContactData.email}`, '_blank');
    }
  };

  if (!successData) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <div className="flex-shrink-0">
          <Icon type="CircleCheckmark" size="L" color="success" />
        </div>
        <div className="ml-3">
          <Text size="XL" weight="bold" color="success">
            {t('modal_confirm_billing.success_title')}
          </Text>
        </div>
      </div>

      {/* Extended Success Message */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 shadow-sm">
        <div className="space-y-4">
          <div className="mb-4">
            <Text size="M" className="text-gray-800 mb-3">
              {t('modal_confirm_billing.success_message_extended')}
            </Text>
          </div>

          {/* Contact Information */}
          <div className="border-t border-green-200 pt-4">
            <Text size="M" weight="semibold" className="text-gray-800 mb-3">
              {t('modal_confirm_billing.contact_info')}
            </Text>
            <div className="space-y-3">
              {exchangeContactData?.phone && (
                <button
                  onClick={handlePhoneClick}
                  className="flex items-center w-full text-blue-600 hover:text-blue-800 transition-colors duration-200 py-2 px-3 rounded-md hover:bg-blue-50"
                >
                  <Text size="M" className="underline">
                    {t('modal_confirm_billing.phone', {
                      phone: exchangeContactData.phone,
                      extension: exchangeContactData.extension || '',
                    })}
                  </Text>
                </button>
              )}
              {exchangeContactData?.whatsapp && (
                <button
                  onClick={handleWhatsAppClick}
                  className="flex items-center w-full text-green-600 hover:text-green-800 transition-colors duration-200 py-2 px-3 rounded-md hover:bg-green-50"
                >
                  <Text size="M" className="underline">
                    {t('modal_confirm_billing.whatsapp', {
                      whatsapp: exchangeContactData.whatsapp,
                    })}
                  </Text>
                </button>
              )}
              {exchangeContactData?.email && (
                <button
                  onClick={handleEmailClick}
                  className="flex items-center w-full text-blue-600 hover:text-blue-800 transition-colors duration-200 py-2 px-3 rounded-md hover:bg-blue-50"
                >
                  <Text size="M" className="underline">
                    {t('modal_confirm_billing.email', {
                      email: exchangeContactData.email,
                    })}
                  </Text>
                </button>
              )}
            </div>
          </div>

          {/* Document Details */}
          {(successData.new_invoice_number || successData.credit_note_number) && (
            <div className="border-t border-green-200 pt-4">
              <Text size="M" weight="semibold" className="text-gray-800 mb-3">
                {t('modal_confirm_billing.response_details')}
              </Text>
              <div className="space-y-3">
                {successData.new_invoice_number && (
                  <div className="flex justify-between items-center py-2">
                    <Text size="M" className="text-green-700 font-medium">
                      {t('modal_confirm_billing.invoice_number')}
                    </Text>
                    <Text size="L" weight="bold" className="text-green-800">
                      {successData.new_invoice_number}
                    </Text>
                  </div>
                )}
                {successData.credit_note_number && (
                  <div className="flex justify-between items-center py-2">
                    <Text size="M" className="text-blue-700 font-medium">
                      {t('modal_confirm_billing.credit_note_number')}
                    </Text>
                    <Text size="L" weight="bold" className="text-blue-800">
                      {successData.credit_note_number}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-center pt-4">
        <Button
          buttonType="button"
          text={t('modal_confirm_billing.close_button')}
          variant="accent"
          isRounded
          withHeight
          onClick={() => onClose(true)}
        />
      </div>
    </div>
  );
};

BillingSuccess.propTypes = {
  successData: PropTypes.shape({
    new_invoice_number: PropTypes.string,
    credit_note_number: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BillingSuccess;
